/**
 * Profile Hooks
 * React Query hooks for profile management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { profileApi } from '@/api'
import type { UserProfileUpdate, UserPasswordChange } from '@/types/user'

// Query keys
export const profileKeys = {
  all: ['profile'] as const,
  me: () => [...profileKeys.all, 'me'] as const,
}

/**
 * Get current user profile
 */
export function useMyProfile() {
  return useQuery({
    queryKey: profileKeys.me(),
    queryFn: () => profileApi.getMyProfile(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Update current user profile
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UserProfileUpdate) => profileApi.updateMyProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.me() })
      // Also invalidate auth /me query if it exists
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
  })
}

/**
 * Change password
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (data: UserPasswordChange) => profileApi.changePassword(data),
  })
}

/**
 * Upload profile picture
 */
export function useUploadAvatar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (file: File) => profileApi.uploadAvatar(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.me() })
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
  })
}

/**
 * Remove profile picture
 */
export function useRemoveAvatar() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => profileApi.removeAvatar(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.me() })
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
  })
}
